import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Download, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { useDailyReviewStore } from '@/stores/dailyReview'
import { api } from '@/lib/api'
import type { ReviewShootDetail, VariantReviewInput } from '@/types'
import { VariantPanel } from './VariantPanel'
import { formatBytes } from './constants'

function Collapsible({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-100 rounded">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {title}
      </button>
      {open && <div className="px-2 pb-2">{children}</div>}
    </div>
  )
}

const emptyReview = (): VariantReviewInput => ({
  rating: null,
  post_processing_pct: 0,
  changes: [],
  note: null,
})

export function ShootCard({ shoot, batchId }: { shoot: ReviewShootDetail; batchId: string }) {
  const { user } = useAuthStore()
  const { saveShootReview, savingShootIds } = useDailyReviewStore()
  const myEmail = user?.email ?? ''

  const initialMyReviews = useMemo(() => {
    const mine: Record<string, VariantReviewInput> = {}
    for (const review of shoot.reviews) {
      if (review.reviewer_email === myEmail) {
        mine[String(review.variant)] = {
          rating: review.rating,
          post_processing_pct: review.post_processing_pct,
          changes: review.changes,
          note: review.note,
        }
      }
    }
    return mine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoot.id])

  const [myReviews, setMyReviews] = useState<Record<string, VariantReviewInput>>(initialMyReviews)
  const [dirty, setDirty] = useState(false)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-save: flush 1.5s after the last edit (comments included), so
  // reviewers never lose feedback by forgetting the button.
  useEffect(() => {
    if (!dirty) return
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(async () => {
      await saveShootReview(shoot.id, myReviews)
      setDirty(false)
    }, 1500)
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myReviews, dirty])

  const variants = useMemo(() => {
    const nums = new Set<number>()
    for (const asset of shoot.assets) {
      if (asset.variant && (asset.role === 'canonical_variant' || asset.role === 'raw_variant')) {
        nums.add(asset.variant)
      }
    }
    for (const key of Object.keys(shoot.variant_results)) nums.add(Number(key))
    return [...nums].sort()
  }, [shoot])

  const images = shoot.assets.filter((a) => ['face', 'panel', 'composite'].includes(a.role))
  const saving = savingShootIds.has(shoot.id)

  const updateVariant = (variant: number, review: VariantReviewInput) => {
    setMyReviews((prev) => ({ ...prev, [String(variant)]: review }))
    setDirty(true)
  }

  const save = async () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    await saveShootReview(shoot.id, myReviews)
    setDirty(false)
  }

  const downloadShoot = async () => {
    const url = await api.getShootDownloadUrl(batchId, shoot.id)
    window.open(url, '_blank')
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" id={shoot.slug}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-sm">{shoot.slug}</h3>
        {shoot.style && (
          <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{shoot.style}</span>
        )}
        {shoot.human_mode && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{shoot.human_mode}</span>
        )}
        {shoot.shot_mode && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{shoot.shot_mode}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={downloadShoot}
            className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
          >
            <Download className="h-3 w-3" /> all files
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium ${
              dirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400'
            }`}
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            {saving ? 'Saving…' : dirty ? 'Save now' : 'Saved'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {variants.map((variant) => {
          const canonical =
            shoot.assets.find((a) => a.role === 'canonical_variant' && a.variant === variant) ?? null
          const raws = shoot.assets.filter((a) => a.role === 'raw_variant' && a.variant === variant)
          const others = shoot.reviews.filter(
            (r) => r.variant === variant && r.reviewer_email !== myEmail
          )
          return (
            <VariantPanel
              key={variant}
              variant={variant}
              passFail={shoot.variant_results[String(variant)]?.result ?? null}
              canonical={canonical}
              rawVariants={raws}
              myReview={myReviews[String(variant)] ?? emptyReview()}
              otherReviews={others}
              onChange={(review) => updateVariant(variant, review)}
            />
          )
        })}
      </div>

      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <div key={img.id} className="relative group">
              {/* preview_url renders inline (no Content-Disposition) — opens full-res in a new tab */}
              <a
                href={img.preview_url ?? '#'}
                target="_blank"
                rel="noreferrer"
                title={`${img.filename} ${formatBytes(img.file_size)} — click to view full size`}
              >
                {img.preview_url && (
                  <img
                    src={img.thumbnail_url ?? img.preview_url}
                    alt={img.filename}
                    loading="lazy"
                    className="h-24 rounded border border-gray-200 object-cover"
                  />
                )}
              </a>
              {img.download_url && (
                <a
                  href={img.download_url}
                  className="absolute bottom-1 right-1 hidden group-hover:flex items-center justify-center h-5 w-5 rounded bg-black/60 text-white text-xs"
                  title="Download file"
                >
                  ⬇
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1">
        {shoot.prompt && (
          <Collapsible title="Seedance prompt">
            <pre className="text-xs whitespace-pre-wrap max-h-64 overflow-auto bg-gray-50 p-2 rounded">
              {shoot.prompt}
            </pre>
          </Collapsible>
        )}
        {Object.keys(shoot.idea).length > 0 && (
          <Collapsible title="idea.json">
            <pre className="text-xs whitespace-pre-wrap max-h-64 overflow-auto bg-gray-50 p-2 rounded">
              {JSON.stringify(shoot.idea, null, 2)}
            </pre>
          </Collapsible>
        )}
        {Object.keys(shoot.subtitles).length > 0 && (
          <Collapsible title="Subtitles">
            <div className="space-y-2">
              {Object.entries(shoot.subtitles).map(([key, content]) => (
                <div key={key}>
                  <div className="text-xs font-medium text-gray-500">{key}</div>
                  <pre className="text-xs whitespace-pre-wrap max-h-48 overflow-auto bg-gray-50 p-2 rounded">
                    {content}
                  </pre>
                </div>
              ))}
            </div>
          </Collapsible>
        )}
      </div>
    </div>
  )
}
